import { Component, OnInit } from '@angular/core';
import {
  EventPricingMatrix,
  EventScheduleException,
  EventType,
  Roster,
  getExceptionCancellationWarning
} from '../../../../../../../models/event-type';
import { ActivatedRoute, Router } from '@angular/router';
import { AccountService } from '../../../../../../../services/account/account.service';
import { EventTypeService } from '../../../../../../../services/event-type/event-type.service';
import { LocationArea } from '../../../../../../../models/location';
import { LocationService } from '../../../../../../../services/location/location.service';
import { ScheduleType } from '../../../../../../../models/event-type-enums';
import { dateStringFromISO8601 } from '../../../../../../../utils';
import { TaxRate } from '../../../../../../../models/tax-rate';
import { TaxRateService } from '../../../../../../../services/tax-rate/tax-rate.service';
import { Membership } from '../../../../../../../models/membership';
import { MembershipService } from '../../../../../../../services/membership/membership.service';
import { MembershipPurchaseType, MembershipStatus } from '../../../../../../../models/membership-enums';

/**
 * What a schedule exception does with the days it lists. Derived from the
 * exception rather than stored on it: one with no time slots only clears its
 * days, one with time slots also creates an event at each of them (PRO-1508).
 */
enum ExceptionMode {
  cancelEvents = 'CANCEL',
  replaceEvents = 'REPLACE'
}

@Component({
  selector: 'app-event-type-edit-schedule-exception',
  templateUrl: './event-type-edit-schedule-exception.component.html',
  styleUrls: ['./event-type-edit-schedule-exception.component.css']
})
export class EventTypeEditScheduleExceptionComponent implements OnInit {
  public eventType: EventType = null;
  public pricingMatrices: EventPricingMatrix[] = null;

  public isNewScheduleException: boolean;
  public scheduleException: EventScheduleException = {
    id: null,
    url: '',
    name: '',
    roster_id: null,
    roster_name: null,
    location_id: null,
    location_name: '',
    location_url: '',
    location_area_id: null,
    // Unset until the exception is set to run events of its own for them to
    // shape (PRO-1433). Switching back to cancelling only hides the fields, so
    // what was entered stays here - but a cancellation is always SAVED with all
    // three null; see submitScheduleException().
    duration_minutes: null,
    capacity: null,
    rolling_days: null,
    booking_open_time: '00:00',
    pricing_matrix_id: null,
    tax_rate_id: null,
    tax_rate_name: null,
    benefit_membership_id: null,
    benefit_membership_name: null,
    benefit_membership_additional_days: 0,
    time_slots: [],
    // Days starts off with one empty day because at least one is required.
    // (This saves the user from having to click the "Add" button)
    dates: [],
    created_at: '',
    updated_at: ''
  };
  public exceptionMode: ExceptionMode = ExceptionMode.cancelEvents;
  public selectedRosterLocationId: number | null = null;
  public dayToAdd = '';
  public timeSlotToAdd = '';

  // What the exception did when it was loaded, so that turning a replacement
  // into a cancellation can be confirmed before it destroys anything.
  private loadedExceptionMode: ExceptionMode = ExceptionMode.cancelEvents;

  public locationAreaList: LocationArea[];
  public rosters: Roster[];
  public taxRateList: TaxRate[];
  public benefitMembershipList: Membership[];

  public loadingEventType = true;
  public loadingLocationAreas = true;
  public loadingRosters = false;
  public loadingTaxRates = false;
  public loadingBenefitMemberships = false;
  public loadingPricingMatrices = true;
  public loadingScheduleException = false;
  public loadingSave = false;

  public notFound = false;
  public errorMessage = '';
  public errors: any = {};
  public serverError: number = null;

  public readonly ScheduleType = ScheduleType;
  public readonly ExceptionMode = ExceptionMode;
  public readonly dateStringFromISO8601 = dateStringFromISO8601;

  constructor(
    public locationService: LocationService,
    private route: ActivatedRoute,
    private router: Router,
    private accountService: AccountService,
    private eventTypeService: EventTypeService,
    private taxRateService: TaxRateService,
    private membershipService: MembershipService
  ) {}

  ngOnInit() {
    const routeParams = this.route.snapshot.params;
    const eventTypeIdInt = parseInt(routeParams.eventTypeId, 10);
    if (isNaN(eventTypeIdInt)) {
      this.notFound = true;
      return;
    }

    this.loadLocationAreas();

    this.loadEventType(eventTypeIdInt);
    this.loadPricingMatrices(eventTypeIdInt);

    this.isNewScheduleException = !routeParams.hasOwnProperty('scheduleExceptionId');

    // Get the schedule to edit, if an ID was provided.
    if (!this.isNewScheduleException) {
      const scheduleExceptionIdInt = parseInt(routeParams.scheduleExceptionId, 10);
      if (isNaN(scheduleExceptionIdInt)) {
        this.notFound = true;
        return;
      }

      this.loadScheduleException(eventTypeIdInt, scheduleExceptionIdInt);
    } else {
      this.scheduleException.location_id = this.accountService.getMyAccount().default_gym_id;
    }
  }

  public loadLocationAreas() {
    this.loadingLocationAreas = true;

    this.serverError = null;

    this.locationService
      .getLocationAreas(
        1,
        100,
        {}
        )
      .then(response => { this.locationAreaList = response.results; })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingLocationAreas = false; });
  }

  public isLoading() {
    return (
      this.loadingEventType ||
      this.loadingPricingMatrices ||
      this.loadingScheduleException ||
      this.loadingSave
    );
  }

  public save() {
    this.errorMessage = '';
    this.errors = {};

    // Remove any empty string dates and time slots before submitting.
    this.scheduleException.dates = this.scheduleException.dates.filter(
      (dateStr) => (dateStr.trim() !== '')
    );
    this.scheduleException.time_slots = this.scheduleException.time_slots.filter(
      (timeStr) => (timeStr.trim() !== '')
    );

    // Saving this with no time slots would quietly store a cancellation, which
    // is the opposite of what the chosen mode says (PRO-1508).
    if (
      this.isReplacementMode() &&
      (this.scheduleException.time_slots.length === 0)
    ) {
      this.errors = {
        time_slots:
          'Add at least one time slot, or choose “Cancel the usual events” '
          + 'above.'
      };
      this.errorMessage = 'Please correct the errors above and try again.';
      return;
    }

    this.loadingSave = true;

    if (!this.isSwitchingToCancellation()) {
      this.submitScheduleException();
      return;
    }

    this.eventTypeService
      .getEventScheduleExceptionDeletionPreview(
        this.eventType.id,
        this.scheduleException.id
      )
      .then(preview => {
        const warning = getExceptionCancellationWarning(preview);
        if (warning && !confirm(`${warning} Are you sure you want to continue?`)) {
          this.loadingSave = false;
          return;
        }
        this.submitScheduleException();
      })
      .catch(() => {
        // The preview is advisory — if it cannot be loaded, save rather than
        // blocking the change entirely, as the deletion flow does.
        this.submitScheduleException();
      });
  }

  /**
   * Whether the exception is being saved as a cancellation having been loaded
   * as a replacement, and so is about to destroy the events it created
   * (PRO-1508).
   */
  private isSwitchingToCancellation(): boolean {
    return (
      !this.isNewScheduleException &&
      (this.loadedExceptionMode === ExceptionMode.replaceEvents) &&
      !this.isReplacementMode()
    );
  }

  /**
   * Write the exception to the API and return to its event type's list.
   * Assumes loadingSave is already set.
   */
  private submitScheduleException() {
    // A cancellation creates no events, so the event-shaping fields the form
    // hides in that mode are not submitted either: the API rejects a
    // half-filled set, and an area can belong to another location, with
    // nothing on screen to correct (PRO-1433). Its time slots go the same way,
    // which is what turns the mode into stored behaviour (PRO-1508). Cleared
    // on the way out only, so everything returns if the mode is switched back.
    // The benefit membership goes too: a blackout grants nothing. The other
    // deferred fields are location-independent and optional or defaulted;
    // getDeferredFieldErrors() is the backstop.
    const exceptionToSave = this.isReplacementMode()
      ? this.scheduleException
      : {
          ...this.scheduleException,
          time_slots: [],
          location_area_id: null,
          duration_minutes: null,
          capacity: null,
          rolling_days: null,
          benefit_membership_id: null,
          benefit_membership_additional_days: 0
        };

    let savePromise: Promise<EventScheduleException>;
    if (this.isNewScheduleException) {
      savePromise = this.eventTypeService.addEventScheduleException(
        this.eventType.id,
        exceptionToSave
      );
    } else {
      savePromise = this.eventTypeService.updateEventScheduleException(
        exceptionToSave
      );
    }

    savePromise
      .then(savedException => {
        this.router.navigate([
          '/events/setup/edit/' + this.eventType.id.toString() + '/schedule-exceptions'
        ]);
      })
      .catch(reason => {
        if (reason.status === 400) {
          this.errors = reason.error;
          this.errorMessage = 'Please correct the errors above and try again.';
        } else if (reason.status === 403) {
          if (this.isNewScheduleException) {
            this.errors = 'You do not have permission to add schedule exceptions.';
          } else {
            this.errors = 'You do not have permission to modify this schedule exception.';
          }
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingSave = false; });
  }

  /**
   * Whether this schedule exception applies to every roster of its event type
   * at its location, rather than to one named roster (PRO-1432). Only rolling
   * event types have rosters to choose between at all.
   */
  public isAllRostersException(): boolean {
    return (
      (this.eventType !== null) &&
      (this.eventType.schedule_type === ScheduleType.rolling) &&
      (this.scheduleException.roster_id === null)
    );
  }

  /**
   * React to the cancel-or-replace choice changing. The errors from the last
   * save attempt were raised against the fields of the mode being left, and
   * their inline half unmounts with those fields - leaving the summary alert
   * pointing at nothing - so the whole set is dropped (PRO-1508).
   */
  public handleExceptionModeChange() {
    this.errors = {};
    this.errorMessage = '';
  }

  /**
   * Whether the exception is set to run different events on its days rather
   * than simply cancelling the usual ones (PRO-1508). An all-rosters exception
   * cannot create events at all, so it is locked to cancelling.
   */
  public isReplacementMode(): boolean {
    return (
      (this.exceptionMode === ExceptionMode.replaceEvents) &&
      !this.isAllRostersException()
    );
  }

  /**
   * Server errors reported against fields the form has hidden (PRO-1433). Their inline errors land inside the hidden block, so they are
   * surfaced here instead; save() should prevent them, but an error nobody
   * can read is the worst way to find out otherwise.
   */
  public getDeferredFieldErrors(): string[] {
    if (this.isReplacementMode()) { return []; }

    return [
      'time_slots',
      'location_area_id',
      'rolling_days',
      'booking_open_time',
      'capacity',
      'pricing_matrix_id',
      'tax_rate_id',
      'benefit_membership_id',
      'benefit_membership_additional_days',
      'duration_minutes'
    ]
      .map(fieldName => this.errors[fieldName])
      .filter(message => !!message);
  }

  public getLocationAreasForLocation(): LocationArea[] {
    if (this.loadingLocationAreas) { return []; }

    return this.locationAreaList.filter(locationArea => locationArea.gym_id === this.scheduleException.location_id);
  }

  /**
   * React to the roster selection changing. An "All Rosters" exception can only
   * black its days out (PRO-1432), so selecting one settles the mode back on
   * cancelling rather than leaving a disabled radio showing as chosen.
   */
  public handleRosterChange() {
    this.setLocationToRosterDefault();

    if (this.isAllRostersException()) {
      this.exceptionMode = ExceptionMode.cancelEvents;
      // Setting the model directly emits no ngModelChange, so the radio's own
      // handler does not run for a mode forced from here.
      this.handleExceptionModeChange();
    }
  }

  public setLocationToRosterDefault() {
    this.setRosterLocationId();
    if (this.selectedRosterLocationId) {
      this.scheduleException.location_id = this.selectedRosterLocationId;
    }
  }

  public benefitMembershipRetired(id: number | null): boolean {
    if (id === null || id === undefined) {
      return false;
    }
    return !this.benefitMembershipList ||
      !this.benefitMembershipList.some(membership => membership.id === id);
  }

  public loadBenefitMemberships() {
    this.loadingBenefitMemberships = true;

    this.membershipService
      .getMemberships(
        1,
        3000,
        {
          status: MembershipStatus.active,
          purchase_type: MembershipPurchaseType.benefit,
        }
      )
      .then(response => {
        this.benefitMembershipList = response.results;
      })
      .catch(reason => {
        this.serverError = reason.status;
      })
      .finally(() => { this.loadingBenefitMemberships = false; });
  }

  public loadTaxRates() {
    this.loadingTaxRates = true;

    this.taxRateService
      .getTaxRates(1, 100)
      .then(response => {
        this.taxRateList = response.results;
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingTaxRates = false; });
  }

  public getEventTypeTaxRate(): string {
    if (this.eventType.tax_rate_name) {
      let taxRateText = this.eventType.tax_rate_name;
      if (this.taxRateList) {
        for (const taxRate of this.taxRateList) {
          if (taxRate.id === this.eventType.tax_rate_id) {
            taxRateText += ' - ' + taxRate.rate_percent + '%';
          }
        }
      }
      return taxRateText;
    }
    else { return 'No Tax Rate'; }
  }

  /**
   * Load the event type with the given ID.
   *
   * @param eventTypeId - The API ID of the event type.
   */
  private loadEventType(eventTypeId: number) {
    this.loadingEventType = true;

    this.eventTypeService
      .getEventType(eventTypeId)
      .then(eventType => {
        this.eventType = eventType;
        this.settleExceptionMode();
        if (this.eventType.schedule_type === ScheduleType.rolling) {
          this.loadRosters();
        } else {
          this.loadTaxRates();
        }
        if (this.eventType.schedule_type === ScheduleType.repeating) {
          this.loadBenefitMemberships();
        }
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else if (reason.status === 404) {
          this.notFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingEventType = false; });
  }

  private loadPricingMatrices(eventTypeId: number) {
    this.loadingPricingMatrices = true;

    this.eventTypeService
      .getEventPricingMatrices(eventTypeId, 1, 100)
      .then(pricingMatricesResponse => {
        this.pricingMatrices = pricingMatricesResponse.results;
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else if (reason.status === 404) {
          this.notFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingPricingMatrices = false; });
  }

  private loadScheduleException(eventTypeId: number,
                                scheduleExceptionId: number) {
    this.loadingScheduleException = true;

    this.eventTypeService
      .getEventScheduleException(eventTypeId, scheduleExceptionId)
      .then(scheduleException => {
        this.scheduleException = scheduleException;
        this.settleExceptionMode();
        if (!this.loadingEventType && !this.loadingRosters && (this.eventType.schedule_type === ScheduleType.rolling)) {
          this.setRosterLocationId();
        }
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else if (reason.status === 404) {
          this.notFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingScheduleException = false; });
  }

  private loadRosters() {
    this.loadingRosters = true;

    this.serverError = null;

    this.eventTypeService
      .getRosters(
        1,
        3000,
        {event_type_id: this.eventType.id}
        )
      .then(response => {
        this.rosters = response.results;
        if (!this.loadingScheduleException && (this.eventType.schedule_type === ScheduleType.rolling)) {
          this.setRosterLocationId();
        }
      })
      .catch(reason => {
        this.serverError = reason.status;
      })
      .finally(() => { this.loadingRosters = false; });
  }

  /**
   * Settle the cancel-or-replace mode on what the loaded exception actually
   * does: one carrying time slots replaces its days' events, one carrying
   * none only cancels them (PRO-1508). Called from both loaders because either
   * may finish last, and the mode depends on both.
   *
   * An all-rosters exception is pinned to cancelling whatever it carries
   * (PRO-1432): the API refuses to create such a row with time slots, but the
   * Django admin does not prevent it, and cancelling mode drops them on save
   * rather than echoing them back to be rejected.
   */
  private settleExceptionMode() {
    this.exceptionMode = (
      !this.isAllRostersException() &&
      (this.scheduleException.time_slots.length > 0)
    )
      ? ExceptionMode.replaceEvents
      : ExceptionMode.cancelEvents;

    this.loadedExceptionMode = this.exceptionMode;
  }

  private setRosterLocationId() {
    for (const roster of this.rosters) {
      if (roster.id === this.scheduleException.roster_id) {
        this.selectedRosterLocationId = roster.location_id;
        return;
      }
    }
    this.selectedRosterLocationId = null;
  }
}
